import { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import socketClient from 'socket.io-client';
import moment from 'moment';
import { Carousel } from 'react-responsive-carousel';
import pencil from "../assets/images/pencil.png";
import brokenHeart from '../assets/images/broken-heart.png';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import styles from './Fundraiser.module.css';
import { getFundraiserApi } from '../api/fundraisers.js';
import findArrayIndex from '../utils/findArrayIndex.js';
import LoggedInNav from '../components/logged-in/LoggedInNav.jsx';
import LoggedOutNav from '../components/logged-out/LoggedOutNav.jsx';
import FormHeader from '../components/form/FormHeader.jsx';
import InfoSection from '../components/fundraiser/InfoSection.jsx';
import BottomViewHeaders from "../components/fundraiser/BottomViewHeaders";
import Comments from '../components/fundraiser/Comments.jsx';
import Updates from '../components/fundraiser/Updates';
import Changes from "../components/fundraiser/Changes";
import SuperUserActions from "../components/fundraiser/SuperUserActions";
import RequestAction from "../components/fundraiser/RequestAction";
import RequestStatus from '../components/fundraiser/RequestStatus';
import CountdownClock from '../components/fundraiser/CountdownClock.jsx';
import ActionCard from '../components/fundraiser/ActionCard.jsx';
import DonateModal from '../components/modals/DonateModal.jsx';
import OfflineDonateModal from "../components/modals/OfflineDonateModal";
import ShareModal from '../components/modals/ShareModal.jsx';
import DonarsModal from '../components/modals/DonarsModal.jsx';
import AddUpdateModal from '../components/modals/AddUpdateModal';
import PhotoModal from '../components/modals/PhotoModal';
import EditNameModal from "../components/modals/EditNameModal";
import EditDescriptionModal from "../components/modals/EditDescriptionModal";
import EditEndAtModal from "../components/modals/EditEndAtModal";
import EditGoalModal from "../components/modals/EditGoalModal";
import ViewDescriptionModal from "../components/modals/ViewDescriptionModal";

const io = socketClient(process.env.REACT_APP_BACK_END_DOMAIN === 'https://giverist.com/api' ? 'https://giverist.com' : process.env.REACT_APP_BACK_END_DOMAIN, {
	extraHeaders: {
		token: localStorage.getItem('api-token')
	},
    path: process.env.REACT_APP_BACK_END_DOMAIN === 'https://giverist.com/api' ? '/api/socket.io/' : '/socket.io/'
});

const Fundraiser = () => {
	const { fundraiserId } = useParams();
	const { user } = useOutletContext();
	const [fundraiser, setFundraiser] = useState({});
	const [fundraiserNotFound, setFundraiserNotFound] = useState(false);
	const [status, setStatus] = useState('');
    const [bottomView, setBottomView] = useState('comments');
	const [comments, setComments] = useState([]);
    const [updates, setUpdates] = useState([]);
	const [changes, setChanges] = useState([]);
	const [showSuperUserActions, setShowSuperUserActions] = useState(false);
	const [showRequestAction, setShowRequestAction] = useState(false);
	const [showDonateModal, setShowDonateModal] = useState(false);
	const [showOfflineDonateModal, setShowOfflineDonateModal] = useState(false);
	const [showShareModal, setShowShareModal] = useState(false);
	const [showDonarsModal, setShowDonarsModal] = useState(false);
    const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
    const [showPhotoModal, setShowPhotoModal] = useState(false);
	const [photoModalUrls, setPhotoModalUrls] = useState([]);
    const [photoModalUrl, setPhotoModalUrl] = useState('');
	const [photoModalIndex, setPhotoModalIndex] = useState(null);
	const [canBeUpdated, setCanBeUpdated] = useState(false);
	const [showNameUpdateModal, setShowNameUpdateModal] = useState(false);
	const [showDescriptionUpdateModal, setShowDescriptionUpdateModal]= useState(false);
	const [showEndAtUpdateModal, setShowEndAtUpdateModal]= useState(false);
	const [showGoalUpdateModal, setShowGoalUpdateModal] = useState(false);
	const [showViewDescriptionModal, setShowViewDescriptionModal] = useState(false);
	const [viewDecriptionText, setViewDescriptionText] = useState('');

	const addDonation = (donation) => {
		const updatedDonationObject = {...donation, created_at: moment().toISOString()};
		setFundraiser((previous) => {
			const previousDonations = previous.donations;
			const updatedTotalDonationsAmount = Number(previous.total_donations_amount) + Number(updatedDonationObject.amount);
			const index = findArrayIndex(previousDonations, 'id', updatedDonationObject.id);
			return index === false ? {...previous, total_donations_amount: updatedTotalDonationsAmount, donations: [...previousDonations, updatedDonationObject]} : previous;
		});
	};

	const getFundraiser = async () => {
		const response = await getFundraiserApi(fundraiserId);
		let body;
		try{
			body = await response.json();
		}
		catch(e){}
		if(response && response.ok){
			setFundraiser(body.data.fundraiser);
		}
		else{
			setFundraiserNotFound(true)
		}
	};

	const sortImages = (a, b) => {
		if(a.sort_order > b.sort_order){
			return 1;
		}
		else if(a.sort_order < b.sort_order){
			return -1;
		}
		return 0;
	};

    const openPhotoModal = (urls, url) => {
		setPhotoModalUrls(urls);
        setPhotoModalUrl(process.env.REACT_APP_CLOUDFRONT_S3_DOMAIN + url);
		setPhotoModalIndex(urls.indexOf(url));
        setShowPhotoModal(true);
    };

    const setInitialBottomView = () => {
        if(comments.length === 0 && (fundraiser.user_id === user.id || updates.length > 0)){
            setBottomView('updates');
        }
		else if(comments.length === 0 && updates.length === 0 && changes.length > 0){
			setBottomView('changes');
		}
        else{
            setBottomView('comments');
        }
    };

	const parseComments = () => {
		const commentsArray = []
		if(fundraiser.donations?.length > 0){
			for(let i = 0; i < fundraiser.donations.length; i++){
				if(fundraiser.donations[i].comment){
					commentsArray.push(fundraiser.donations[i]);
				}
			}
		}
		setComments(commentsArray);
	};

    const parseUpdates = () => {
        const updatesArray = [];
        if(fundraiser.updates?.length > 0){
            for(let i = 0; i < fundraiser.updates.length; i++){
                updatesArray.push(fundraiser.updates[i]);
            }
        }
        setUpdates(updatesArray);
    };

	const parseChanges = () => {
		const changesArray = [];
        if(fundraiser.changes?.length > 0){
            for(let i = 0; i < fundraiser.changes.length; i++){
                changesArray.push(fundraiser.changes[i]);
            }
        }
        setChanges(changesArray);
	};

	const showBottomViewHeaders = () => {
		if(([comments.length > 0, updates.length > 0, changes.length > 0].filter(Boolean).length >= 2) ||
		  ((comments.length > 0 || changes.length > 0) && updates.length === 0 && fundraiser.user_id === user.id)){
			return true;
		}
		else{
			return false;
		}
	};

    const addUpdate = (update) => {
        setFundraiser({...fundraiser, updates: [...fundraiser.updates, update]});
        setTimeout(() => {
            setBottomView('updates');
        }, 50);
    };

	const addChange = (changeHistoryId, type, newValue) => {
		const previousValue = fundraiser[type];
		setFundraiser({...fundraiser, [type]: newValue, changes: [...fundraiser.changes, {
			id: changeHistoryId,
			fundraiser_id: fundraiserId,
			user_id: user.id,
			type: type,
			previous_value: previousValue,
			new_value: newValue,
			created_at: moment().toISOString()
		}]});
	};

	const closeModals = () => {
        setShowDonateModal(false);
		setShowOfflineDonateModal(false);
		setShowShareModal(false);
        setShowDonarsModal(false);
        setShowAddUpdateModal(false);
        setShowPhotoModal(false);
        setPhotoModalUrl('');
		setShowNameUpdateModal(false);
		setShowDescriptionUpdateModal(false);
		setShowEndAtUpdateModal(false);
		setShowGoalUpdateModal(false);
		setShowViewDescriptionModal(false);
		setViewDescriptionText('');
	};

    const escapeKeyDown = (event) => {
        if(event.key === 'Escape'){
            closeModals();
        }
    };

	useEffect(() => {
        window.addEventListener('keydown', escapeKeyDown);
		io.on('fundraiser/' + fundraiserId, (data) => {
			addDonation(data);
		});
		getFundraiser();
        return () => {
            window.removeEventListener('keydown', escapeKeyDown);
        };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		document.title = fundraiser.name ? fundraiser.name : 'Fundraiser';
	}, [fundraiser]);

	useEffect(() => {
		parseComments();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fundraiser?.donations]);

	useEffect(() => {
		parseUpdates();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fundraiser?.updates]);

	useEffect(() => {
		parseChanges();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fundraiser?.changes]);

	useEffect(() => {
		if(user.is_super_user && (status === 'in-progress' || fundraiser.donations?.length > 0)){
			setShowSuperUserActions(true);
		}
		else{
			setShowSuperUserActions(false);
		}
		if(fundraiser.organization_id === user.organization_id && fundraiser.direct_payment_status === 'requested' &&
		   (fundraiser.total_donations_amount > 0 || status !== 'ended')){
			setShowRequestAction(true);
		}
		if(fundraiser){
			if(status === 'in-progress' && (fundraiser.user_id === user.id || fundraiser.organization_id === user.organization_id || user.is_super_user)){
				setCanBeUpdated(true);
			}
			else{
				setCanBeUpdated(false);
			}
		}
	}, [fundraiser, status, user]);

	useEffect(() => {
		if(showRequestAction){
			if(window.location.hash === '#request-action'){
				document.getElementById('request-action').scrollIntoView();
			}
		}
	}, [showRequestAction]);

    useEffect(() => {
        setInitialBottomView();
		// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [comments, changes]);

	const leftKeyDown = () => {
		if(showPhotoModal === true){
			let newIndex = photoModalIndex - 1;
			if(typeof photoModalUrls[newIndex] === 'undefined'){
				newIndex = photoModalUrls.length - 1;
			}
			setPhotoModalUrl(process.env.REACT_APP_CLOUDFRONT_S3_DOMAIN + photoModalUrls[newIndex]);
			setPhotoModalIndex(newIndex);
		}
	};

	const rightKeyDown = () => {
		if(showPhotoModal === true){
			let newIndex = photoModalIndex + 1;
			if(typeof photoModalUrls[newIndex] === 'undefined'){
				newIndex = 0;
			}
			setPhotoModalUrl(process.env.REACT_APP_CLOUDFRONT_S3_DOMAIN + photoModalUrls[newIndex]);
			setPhotoModalIndex(newIndex);
		}
	};

	const leftRightKeyDowns = (event) => {
		if(event.key === 'ArrowLeft'){
			leftKeyDown();
		}
		if(event.key === 'ArrowRight'){
			rightKeyDown();
		}
	};

	useEffect(() => {
		window.addEventListener('keydown', leftRightKeyDowns);
		return () => {
			window.removeEventListener('keydown', leftRightKeyDowns);
        };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showPhotoModal, photoModalIndex]);

	return (
		<div>
			{
				user.id ? (
					<LoggedInNav/>
				) : (
					<LoggedOutNav/>
				)
			}
			<div className={styles.container}>
				{
					!fundraiserNotFound ? (
						<div className={styles.fundraiserFound}>
							<div className={styles.leftSide}>
								<div className={styles.headerContainer}>
									<FormHeader className={styles.header} text={fundraiser.name}/>
									{
										canBeUpdated && (
											<img className={classNames(styles.pencil, styles.namePencil)} src={pencil} alt="Pencil" title="Edit Fundraiser Name"
												 onClick={() => setShowNameUpdateModal(true)}/>
										)
									}
								</div>
								<InfoSection fundraiser={fundraiser} setStatus={setStatus}/>
								{
									fundraiser.pictures?.length > 0 && (
										<div className={styles.carouselContainer}>
											<Carousel className={styles.carousel} infiniteLoop={true} showStatus={false} showArrows={false} showIndicators={false}
													  showThumbs={fundraiser.pictures?.length > 1} useKeyboardArrows={true}
                                                      onClickItem={(index, picture) => openPhotoModal(fundraiser.pictures.map(picture => picture.url), picture.key)}>
												{
													fundraiser.pictures.sort(sortImages).map(picture => (
														<div key={picture.url}>
															<img src={process.env.REACT_APP_CLOUDFRONT_S3_DOMAIN + picture.url} alt={picture.url}/>
														</div>
													))
												}
											</Carousel>
										</div>
									)
								}
								<div className={styles.desktopDescriptionContainer}>
									<div className={classNames(styles.description, styles.desktopDescription)}>
										{fundraiser.description}
									</div>
									{
										canBeUpdated && (
											<img className={classNames(styles.pencil, styles.descriptionPencil)} src={pencil} alt="Pencil" title="Edit Fundraiser Description"
												 onClick={() => setShowDescriptionUpdateModal(true)}/>
										)
									}
								</div>
								{
									showBottomViewHeaders() && (
                                        <BottomViewHeaders className={styles.desktopBottomViewHeaders} numOfComments={comments.length} numOfUpdates={updates.length}
														   numOfChanges={changes.length} selectedView={bottomView} setView={setBottomView}
														   isOwnFundraiser={fundraiser.user_id === user.id}/>
									)
                                }
								{
									comments.length > 0 && bottomView === 'comments' && (
										<Comments className={styles.desktopComments} comments={comments}/>
									)
								}
                                {
                                    ((updates.length > 0 || (fundraiser.user_id === user.id)) && bottomView === 'updates') && (
                                        <Updates className={styles.desktopUpdates} updates={updates} isOrganizer={fundraiser.user_id === user.id}
                                                 setShowAddUpdateModal={setShowAddUpdateModal} openPhotoModal={openPhotoModal}/>
                                    )
                                }
								{
									changes.length > 0 && bottomView === 'changes' && (
										<Changes className={styles.desktopComments} changes={changes} setShowViewDescriptionModal={setShowViewDescriptionModal}
											 setViewDescriptionText={setViewDescriptionText}/>
									)
								}
							</div>
							<div className={styles.rightSide}>
								{
									showSuperUserActions && (
										<SuperUserActions className={styles.requestAction} fundraiserId={fundraiser.id} donations={fundraiser.donations} status={status}
													      endFundraiser={() => {
															  setFundraiser({...fundraiser, end_at: (new Date().toISOString())});
															  setStatus('ended');
														  }}
														  updateFundraiserEndAt={(changeHistoryId, endAt) => addChange(changeHistoryId, 'end_at', endAt)}/>
									)
								}
								{
									showRequestAction && (
										<RequestAction className={styles.requestAction} fundraiserId={fundraiser.id}
													   setRequestStatus={(status) => setFundraiser({...fundraiser, direct_payment_status: status})}
													   setShowRequestAction={setShowRequestAction}/>
									)
								}
								{
									user.id === fundraiser.user_id && fundraiser.direct_payment_status !== null && (
										<RequestStatus className={styles.requestStatus} status={fundraiser.direct_payment_status}/>
									)
								}
								{
									(status === 'in-progress' || status === 'ended') && (
										<CountdownClock className={styles.countdownClock} status={status} endAt={fundraiser.end_at} canBeUpdated={canBeUpdated}
														setShowEndAtUpdateModal={setShowEndAtUpdateModal}/>
									)
								}
								<ActionCard fundraiser={fundraiser} status={status} canBeUpdated={canBeUpdated} showDonateModal={() => setShowDonateModal(true)}
											showOfflineDonateModal={() => setShowOfflineDonateModal(true)} showShareModal={() => setShowShareModal(true)}
											setShowDonorsModal={(view) => setShowDonarsModal(view)} setShowGoalUpdateModal={setShowGoalUpdateModal}/>
							</div>
							<div className={styles.mobileDescriptionContainer}>
								<div className={styles.description}>
									{fundraiser.description}
								</div>
								{
									canBeUpdated && (
										<img className={classNames(styles.pencil, styles.descriptionPencil)} src={pencil} alt="Pencil" title="Edit Fundraiser Description"
											 onClick={() => setShowDescriptionUpdateModal(true)}/>
									)
								}
							</div>
							{
								showBottomViewHeaders() && (
                                    <BottomViewHeaders className={styles.mobileBottomViewHeaders} numOfComments={comments.length} numOfUpdates={updates.length}
													   numOfChanges={changes.length} selectedView={bottomView} setView={setBottomView}
													   isOwnFundraiser={fundraiser.user_id === user.id}/>
                                )
                            }
							{
								comments.length > 0 && bottomView === 'comments' && (
									<Comments className={styles.mobileComments} comments={comments}/>
								)
							}
                            {
                                ((updates.length > 0 || (fundraiser.user_id === user.id)) && bottomView === 'updates') && (
                                    <Updates className={styles.mobileUpdates} updates={updates} isOrganizer={fundraiser.user_id === user.id}
                                             setShowAddUpdateModal={setShowAddUpdateModal} openPhotoModal={openPhotoModal}/>
                                )
                            }
							{
								changes.length > 0 && bottomView === 'changes' && (
									<Changes className={styles.mobileComments} changes={changes} setShowViewDescriptionModal={setShowViewDescriptionModal}
											 setViewDescriptionText={setViewDescriptionText}/>
								)
							}
						</div>
					) : (
						<div className={styles.fundraiserNotFound}>
							<img className={styles.brokenHeart} src={brokenHeart} alt="Broken Heart"/>
							<div className={styles.fundraiserNotFoundDescription}>
								The fundraiser you are looking for cannot be found
							</div>
						</div>
					)
				}
			</div>
			{
				showDonateModal && createPortal(
					<DonateModal organization={fundraiser.organization} bottomView={bottomView} setBottomView={setBottomView} close={closeModals}/>, document.body
				)
			}
			{
				showOfflineDonateModal && createPortal(
					<OfflineDonateModal fundraiser={fundraiser} bottomView={bottomView} setBottomView={setBottomView} close={closeModals}/>, document.body
				)
			}
			{
				showShareModal && createPortal(
					<ShareModal close={closeModals}/>, document.body
				)
			}
			{
				showDonarsModal && createPortal(
					<DonarsModal view={showDonarsModal} donations={fundraiser.donations} close={closeModals}/>, document.body
				)
			}
            {
                showAddUpdateModal && createPortal(
                    <AddUpdateModal fundraiserId={fundraiser.id} addUpdate={addUpdate} close={closeModals}/>, document.body
                )
            }
            {
                showPhotoModal && createPortal(
                    <PhotoModal url={photoModalUrl} leftKeyDown={leftKeyDown} rightKeyDown={rightKeyDown} close={closeModals}/>, document.body
                )
            }
			{
                showNameUpdateModal && createPortal(
                    <EditNameModal fundraiser={fundraiser} updateFundraiserName={(changeHistoryId, name) => addChange(changeHistoryId, 'name', name)}
								   close={closeModals}/>, document.body
                )
            }
			{
                showDescriptionUpdateModal && createPortal(
                    <EditDescriptionModal fundraiser={fundraiser} updateFundraiserDescription={(changeHistoryId, description) => addChange(changeHistoryId, 'description', description)}
										  close={closeModals}/>, document.body
                )
            }
			{
                showEndAtUpdateModal && createPortal(
                    <EditEndAtModal fundraiser={fundraiser} updateFundraiserEndAt={(changeHistoryId, endAt) => addChange(changeHistoryId, 'end_at', endAt)}
									close={closeModals}/>, document.body
                )
            }
			{
                showGoalUpdateModal && createPortal(
                    <EditGoalModal fundraiser={fundraiser} updateFundraiserGoal={(changeHistoryId, goalAmount) => addChange(changeHistoryId, 'goal_amount', goalAmount)}
								   close={closeModals}/>, document.body
                )
            }
			{
                showViewDescriptionModal && createPortal(
                    <ViewDescriptionModal description={viewDecriptionText} close={closeModals}/>, document.body
                )
            }
		</div>
	)
};

export default Fundraiser;